import type { Trip } from '@/services/trips';
import { parseTripFirestoreDateField } from '@/services/trips';
import { coerceDate, toLocalDateKey } from '@/utils/dateKeys';
import { getZonedParts, toDateKeyInTimezone, zonedDateTimeToUtcMs } from '@/utils/tripItemSchedule';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Coerce Firestore / template dates to the intended local calendar day. */
export const normalizeTripCalendarDate = (value: unknown): Date | null =>
  parseTripFirestoreDateField(value) ?? coerceDate(value);

export const getTripStartDateKey = (trip: Trip): string => {
  const start = normalizeTripCalendarDate(trip.startDate);
  return start ? toLocalDateKey(start) : toLocalDateKey(new Date());
};

export const getTripEndDateKey = (trip: Trip): string => {
  const end = normalizeTripCalendarDate(trip.endDate);
  return end ? toLocalDateKey(end) : getTripStartDateKey(trip);
};

export const diffTripCalendarDateKeys = (fromKey: string, toKey: string): number => {
  const [fromYear, fromMonth, fromDay] = fromKey.split('-').map((value) => Number.parseInt(value, 10));
  const [toYear, toMonth, toDay] = toKey.split('-').map((value) => Number.parseInt(value, 10));
  return Math.round(
    (Date.UTC(toYear, toMonth - 1, toDay) - Date.UTC(fromYear, fromMonth - 1, fromDay)) / MS_PER_DAY,
  );
};

export const offsetTripCalendarDateKey = (dateKey: string, dayOffset: number): string => {
  const [year, month, day] = dateKey.split('-').map((value) => Number.parseInt(value, 10));
  const next = new Date(year, month - 1, day + dayOffset);
  return toLocalDateKey(next);
};

export const getTripTotalDays = (trip: Trip): number =>
  Math.max(1, diffTripCalendarDateKeys(getTripStartDateKey(trip), getTripEndDateKey(trip)) + 1);

/** Current trip day from destination-local "now" vs trip calendar start. */
export const getTripDayNumberForInstant = (trip: Trip, instant: Date, timeZone: string): number | null => {
  const totalDays = getTripTotalDays(trip);
  const tripStartKey = getTripStartDateKey(trip);
  const tripEndKey = getTripEndDateKey(trip);
  const todayKey = toDateKeyInTimezone(instant, timeZone);
  if (todayKey < tripStartKey || todayKey > tripEndKey) {
    return null;
  }
  return Math.min(totalDays, Math.max(1, diffTripCalendarDateKeys(tripStartKey, todayKey) + 1));
};

export const startOfDayInTimezone = (date: Date, timeZone: string): Date => {
  const parts = getZonedParts(date, timeZone);
  return new Date(zonedDateTimeToUtcMs(parts.year, parts.month, parts.day, 0, 0, 0, timeZone));
};

export const diffCalendarDaysInTimezone = (from: Date, to: Date, timeZone: string): number => {
  const fromDay = startOfDayInTimezone(from, timeZone);
  const toDay = startOfDayInTimezone(to, timeZone);
  return Math.round((toDay.getTime() - fromDay.getTime()) / MS_PER_DAY);
};

/** Trip day index (1-based) from stored itinerary anchors. */
export const getItemTripDayNumber = (trip: Trip, item: { startDate: Date }): number => {
  const itemStart = normalizeTripCalendarDate(item.startDate);
  if (!itemStart) return 1;
  return diffTripCalendarDateKeys(getTripStartDateKey(trip), toLocalDateKey(itemStart)) + 1;
};

/** Itinerary calendar label for a trip day (Jun 28 + day 10 → Jul 7). */
export const getDateKeyForTripDay = (trip: Trip, dayNumber: number): string =>
  offsetTripCalendarDateKey(getTripStartDateKey(trip), Math.max(0, dayNumber - 1));

/** Stable bucket key for grouping/filtering itinerary rows. */
export const getItemTripCalendarDateKey = (trip: Trip, item: { startDate: Date }): string =>
  getDateKeyForTripDay(trip, getItemTripDayNumber(trip, item));

/** Parse a trip calendar date key into Y/M/D parts for wall-clock scheduling. */
export const parseTripCalendarDateKey = (dateKey: string): { year: number; month: number; day: number } => {
  const [year, month, day] = dateKey.split('-').map((value) => Number.parseInt(value, 10));
  return { year, month, day };
};
